"use client";

import Link from "next/link";
import { content } from "@/config/content";
import { useSiteContent } from "@/lib/useSiteContent";

function PillIcon({ type }) {
  const props = {
    width: 14,
    height: 14,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  switch (type) {
    case "calendar":
      return (
        <svg {...props}>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      );
    case "plane":
      return (
        <svg {...props}>
          <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.4-.1.9.3 1.1L11 12l-2 3H6l-2 2 4-1 4-1 2 7.5 2-2-1-3z" />
        </svg>
      );
    case "artist":
      return (
        <svg {...props}>
          <path d="M12 2a5 5 0 015 5c0 2.76-2.24 5-5 5s-5-2.24-5-5a5 5 0 015-5z" />
          <path d="M2 20c0-3.87 3.13-7 7-7h6c3.87 0 7 3.13 7 7" />
        </svg>
      );
    case "shop":
      return (
        <svg {...props}>
          <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <path d="M16 10a4 4 0 01-8 0" />
        </svg>
      );
    default:
      return null;
  }
}

export default function InfoBar({ initialPills = [] }) {
  // 優先用 SSR pre-fetch 的 props（page.js 預傳）；沒有時再 client-side fetch（向後兼容其他用法）
  const shouldClientFetch = initialPills.length === 0;
  const { items: clientItems } = useSiteContent(
    shouldClientFetch ? "hero_pill" : null,
  );
  const items = initialPills.length > 0 ? initialPills : clientItems;
  // Prefer DB-driven pills; fall back to config if DB is empty
  const pills = items.length
    ? items.map((row) => ({
        icon: row.imageUrl || "",
        label: row.title || "",
        href: row.body || "#",
      }))
    : content.hero.infoPills;
  if (!pills?.length) return null;

  return (
    <section
      className=""
      style={{
        backgroundColor: "var(--color-bgSecondary, var(--color-bg))",
        borderBottom: "1px solid var(--color-cardBorder)",
      }}
    >
      <div
        className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3 max-w-3xl mx-auto py-4 md:py-5"
        style={{ padding: "16px var(--spacing-pagePadding)" }}
      >
        {pills.map((pill) => {
          const isExternal = pill.href.startsWith("http");

          const inner = (
            <>
              <PillIcon type={pill.icon} />
              <span>{pill.label}</span>
              {isExternal && (
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ opacity: 0.4 }}
                >
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              )}
            </>
          );

          const className =
            "flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium transition-all hover:scale-105 active:scale-95 min-h-[44px]";
          const style = {
            backgroundColor: "var(--color-cardBg)",
            color: "var(--color-text)",
            border: "1px solid var(--color-cardBorder)",
            borderRadius: "var(--radius-full)",
            fontFamily: "var(--font-body)",
            boxShadow: "var(--shadow-card)",
          };

          if (isExternal) {
            return (
              <a
                key={pill.label}
                href={pill.href}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
                style={style}
              >
                {inner}
              </a>
            );
          }

          return (
            <Link
              key={pill.label}
              href={pill.href}
              className={className}
              style={style}
            >
              {inner}
            </Link>
          );
        })}
      </div>
    </section>
  );
}
